'use client'

import { useMemo } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import AppIcon from '@/components/content/app/AppIcon'
import Award from '@/components/ui/Award'
import CriteriaIcons from '@/components/ui/CriteriaIcons'
import Menu from '@/components/ui/Menu'
import { createLogger } from '@/lib/logger'
import { generateMenu } from '@/helpers/menus'
import { Recommendation } from '@/collections/JunctionTables/AppCategoryRecommendation/constants'
import { cn, getBrandColors, isPopulatedObject } from '@/lib/utils'
import type { MenuItem } from '@/helpers/menus'
import type {
  Category,
  App,
  AppCategoryRecommendation,
  Media,
  ReviewCategoryCriterionRating,
  Review,
} from '@/payload-types'

const logger = createLogger('CompareResult')

const primary = cva('p-8 w-full flex flex-col items-center gap-14 bg-gradient-to-t to-25%', {
  variants: {
    recommendation: {
      efficient: 'text-white p-0 gap-6 bg-transparent',
      [Recommendation.TopPick]: 'from-primary-500/5 shadow-efficient text-primary-500',
      [Recommendation.Recommended]: 'from-gray-500/10 text-gray-500',
      [Recommendation.Capable]: '',
      [Recommendation.NotRecommended]: '',
    },
    overlay: {
      true: 'bg-none shadow-none gap-5 pt-2 md:pt-0 md:gap-10',
    },
  },
})

const secondary = cva(
  'py-8 px-5 w-full flex items-center gap-5 bg-gradient-to-t to-25% rounded-xl border border-black/10',
  {
    variants: {
      recommendation: {
        efficient: 'from-primary-500/5 shadow-efficient text-primary-500',
        [Recommendation.TopPick]: 'from-primary-500/5 shadow-efficient text-primary-500',
        [Recommendation.Recommended]: 'from-gray-500/10 text-gray-500',
        [Recommendation.Capable]: 'from-[#D97706]/5 shadow-capable text-[#D97706]',
        [Recommendation.NotRecommended]: '',
      },
    },
  },
)

type CompareResultsProps = VariantProps<typeof primary> &
  VariantProps<typeof secondary> & {
    apps: App[]
    appCategoryEfficiencyPoints: Record<App['id'], number | undefined>
    category: Category
    reduced?: boolean
    overlay?: boolean
  }

type AppResultProps = {
  app: App
  category: Category
  recommendation: Recommendation
  appCategoryEfficiencyPoints?: number
  overlay?: boolean
  className?: string
}

export default function CompareResult({
  apps,
  appCategoryEfficiencyPoints,
  category,
  reduced,
  overlay,
}: CompareResultsProps) {
  const primaryMenu: MenuItem[] = generateMenu('compare', { apps })

  const primaryResults: {
    app: App
    recommendation: Recommendation
  }[] = []
  const secondaryResults: {
    app: App
    recommendation: Recommendation
  }[] = []

  apps.forEach((app) => {
    const recommendation = (
      app.categoryRecommendations?.docs?.find(
        (doc) =>
          (category?.id &&
            category.id === ((doc as AppCategoryRecommendation).category as Category).id) ||
          category?.id === (doc as AppCategoryRecommendation).category,
      ) as AppCategoryRecommendation
    )?.recommendation as Recommendation

    if (recommendation === Recommendation.Capable || reduced) {
      secondaryResults.push({ app, recommendation })
    } else if (recommendation !== Recommendation.NotRecommended) {
      if (primaryResults.length < 2) {
        primaryResults.push({ app, recommendation })
      } else {
        secondaryResults.unshift({ app, recommendation })
      }
    }
  })

  if (overlay) {
    return primaryResults
      .slice(0, 1)
      .map(({ app, recommendation }) => (
        <PrimaryResult
          key={app.id}
          app={app}
          category={category}
          recommendation={recommendation}
          appCategoryEfficiencyPoints={appCategoryEfficiencyPoints[app.id]}
          overlay={overlay}
        />
      ))
  } else {
    return (
      primaryResults.length > 0 && (
        <div className="relative flex w-full flex-col gap-4">
          {!reduced && (
            <div className="flex w-full flex-col rounded-xl border border-black/10 lg:flex-row">
              {primaryResults.map(({ app, recommendation }, index) => (
                <PrimaryResult
                  key={app.id}
                  app={app}
                  category={category}
                  recommendation={recommendation}
                  appCategoryEfficiencyPoints={appCategoryEfficiencyPoints[app.id]}
                  className={
                    primaryResults.length > 1
                      ? index !== 0
                        ? 'rounded-b-xl border-t border-black/10 lg:rounded-l-none lg:rounded-br-xl lg:border-l lg:border-t-0'
                        : 'rounded-t-xl lg:rounded-r-none lg:rounded-bl-xl'
                      : 'rounded-xl'
                  }
                  overlay={overlay}
                />
              ))}

              <div className="absolute right-3 top-3">
                <Menu items={primaryMenu} align="right" />
              </div>
            </div>
          )}
        </div>
      )
    )
  }
}

export function PrimaryResult({
  app,
  category,
  recommendation,
  appCategoryEfficiencyPoints,
  className,
  overlay,
}: AppResultProps) {
  const isEfficient = recommendation === Recommendation.TopPick

  const populatedReviewCriterionRatings = useMemo(() => {
    const reviewDocs = (app.review as { docs?: (number | Review)[] })?.docs
    const review = reviewDocs?.find((r): r is Review => isPopulatedObject<Review>(r))
    const ratings = review?.reviewCategoryCriterionRating?.docs
    return ratings?.filter((r): r is ReviewCategoryCriterionRating =>
      isPopulatedObject<ReviewCategoryCriterionRating>(r),
    )
  }, [app])

  if (!category) return null

  const appIcon = app.icon as Media
  const appGlyph = app.glyph as Media
  if (!isPopulatedObject<Media>(appIcon) && !isPopulatedObject<Media>(appGlyph)) {
    logger.error('App icon or glyph is not populated in CompareResult (PrimaryResult)')
    return null
  }

  const { backgroundColor } = getBrandColors(app)

  return (
    <div className={cn(primary({ recommendation, className, overlay }))}>
      <Award category={category.name} recommendation={recommendation} overlay={overlay} />

      <div className="flex flex-col items-center">
        <AppIcon
          icon={appIcon}
          glyph={appGlyph}
          name={app.name}
          slug={app.slug}
          background={backgroundColor}
          size={overlay ? 56 : 96}
          className="mb-2"
        />
        <span className={cn('text-14 font-semibold', overlay ? 'text-white' : 'text-gray-950')}>
          {app.name}
        </span>
        <span className={cn('text-13 leading-tight', overlay ? 'text-white' : 'text-white/60')}>
          {appCategoryEfficiencyPoints ?? '-'} efficiency points
        </span>
      </div>

      <div className="hidden md:flex">
        <CriteriaIcons
          app={app}
          categoryId={category.id}
          reviewCriterionRatings={populatedReviewCriterionRatings}
          color={overlay ? undefined : isEfficient ? 'text-primary-500' : 'text-gray-500'}
          horizontalAlign="right"
        />
      </div>
    </div>
  )
}
